'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { ArrowRight, Shield, TrendingUp, Globe, Users, Award, ChevronDown, Building2, Landmark, BarChart3, Lock, CheckCircle, Star, ArrowUpRight, Mail, Phone, MapPin, Menu, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────
interface Campaign {
  id: string;
  name: string;
  issuer: string;
  category: string;
  irr: string;
  tenor: string;
  funded: number;
  target: number;
  raised: number;
  daysLeft: number;
  riskLevel: 'Low' | 'Medium' | 'High';
  tint: string;
  image: string;
}

interface Stat {
  value: string;
  label: string;
  sub: string;
}

interface TeamMember {
  name: string;
  role: string;
  bio: string;
  initials: string;
  color: string;
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const featuredCampaigns: Campaign[] = [
  {
    id: 'camp-001', name: 'Meridian Logistics Hub — Series B', issuer: 'Meridian Infrastructure PLC',
    category: 'Infrastructure', irr: '14.2%', tenor: '5 yrs', funded: 79,
    target: 12500000, raised: 9875000, daysLeft: 23, riskLevel: 'Medium',
    tint: 'from-cyan-900/80 via-cyan-800/40 to-transparent',
    image: '/assets/images/image-1787244773683-1787500365523.png',
  },
  {
    id: 'camp-002', name: 'Ashford Commercial REIT II', issuer: 'Ashford Capital Partners',
    category: 'Real Estate', irr: '11.8%', tenor: '7 yrs', funded: 57,
    target: 25000000, raised: 14200000, daysLeft: 41, riskLevel: 'Low',
    tint: 'from-amber-900/80 via-amber-900/40 to-transparent',
    image: '/assets/images/campaign-real-estate.png',
  },
  {
    id: 'camp-004', name: 'Vantage Private Credit Fund I', issuer: 'Vantage Capital Management',
    category: 'Debt / Credit', irr: '9.4%', tenor: '3 yrs', funded: 30,
    target: 15000000, raised: 4500000, daysLeft: 58, riskLevel: 'Medium',
    tint: 'from-violet-900/80 via-violet-900/40 to-transparent',
    image: '/assets/images/campaign-debt-credit.png',
  },
  {
    id: 'camp-005', name: 'Solaris Renewable Energy Fund III', issuer: 'Solaris Asset Management',
    category: 'Clean Energy', irr: '13.1%', tenor: '7 yrs', funded: 12,
    target: 25000000, raised: 3000000, daysLeft: 72, riskLevel: 'Medium',
    tint: 'from-emerald-900/80 via-emerald-900/40 to-transparent',
    image: '/assets/images/campaign-clean-energy.png',
  },
];

const stats: Stat[] = [
  { value: '$2.4B+', label: 'Capital Deployed', sub: 'Across 180+ campaigns' },
  { value: '12,800+', label: 'Accredited Investors', sub: 'In 34 jurisdictions' },
  { value: '94.7%', label: 'Repayment Rate', sub: 'On matured instruments' },
  { value: '11.6%', label: 'Avg. Net IRR', sub: 'Portfolio-wide, 5-yr avg' },
];

const teamMembers: TeamMember[] = [
  {
    name: 'Khalid Al-Rashidi', role: 'Chief Executive Officer',
    bio: 'Former Managing Director at Goldman Sachs MENA. 20+ years in structured finance and capital markets across GCC and Southeast Asia.',
    initials: 'KR', color: 'bg-emerald-900 text-emerald-300',
  },
  {
    name: 'Priya Nair', role: 'Chief Compliance Officer',
    bio: 'Ex-DFSA Senior Examiner. Architect of CrowdVault\'s regulatory framework across DIFC, ADGM, and MAS jurisdictions.',
    initials: 'PN', color: 'bg-cyan-900 text-cyan-300',
  },
  {
    name: 'James Whitfield', role: 'Chief Investment Officer',
    bio: 'Previously Head of Alternatives at Blackrock EMEA. Oversees deal origination, due diligence, and portfolio risk management.',
    initials: 'JW', color: 'bg-violet-900 text-violet-300',
  },
  {
    name: 'Aisha Bint Saeed', role: 'Head of Investor Relations',
    bio: 'Institutional sales background spanning sovereign wealth funds, family offices, and pension funds across the Middle East.',
    initials: 'AS', color: 'bg-amber-900 text-amber-300',
  },
];

const riskColors: Record<string, string> = {
  Low: 'text-emerald-400 bg-emerald-900/30 border-emerald-800/50',
  Medium: 'text-amber-400 bg-amber-900/30 border-amber-800/50',
  High: 'text-red-400 bg-red-900/30 border-red-800/50',
};

const categoryColors: Record<string, string> = {
  Infrastructure: 'text-cyan-400',
  'Real Estate': 'text-amber-400',
  'Debt / Credit': 'text-violet-400',
  'Clean Energy': 'text-emerald-400',
};

// ─── Component ────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const formatM = (n: number) =>
    n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${(n / 1000).toFixed(0)}K`;

  return (
    <div className="min-h-screen bg-[#0a0f1e] text-[#e2e8f0] font-[var(--font-dm-sans)]">

      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/5"
        style={{ background: `rgba(10,15,30,${Math.min(0.97, 0.6 + scrollY / 300)})`, backdropFilter: 'blur(20px)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
              <Landmark className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="font-semibold text-white tracking-tight">CrowdVault<sup className="text-emerald-400 text-[10px] ml-0.5">™</sup></span>
          </div>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-6 lg:gap-8 text-sm text-[#94a3b8]">
            <a href="#campaigns" className="hover:text-white transition-colors">Campaigns</a>
            <a href="#about" className="hover:text-white transition-colors">About Us</a>
            <a href="#corporate" className="hover:text-white transition-colors">Corporate</a>
            <a href="#contact" className="hover:text-white transition-colors">Contact</a>
            <Link href="/documentation" className="hover:text-white transition-colors">Docs</Link>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <Link href="/login" className="hidden sm:block text-sm text-[#94a3b8] hover:text-white transition-colors px-3 py-2">
              Sign In
            </Link>
            <Link href="/onboarding"
              className="text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-white px-3 sm:px-4 py-2 rounded-lg transition-all hover:shadow-lg hover:shadow-emerald-500/20 whitespace-nowrap">
              Get Started
            </Link>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 rounded-lg text-[#94a3b8] hover:text-white hover:bg-white/5 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/5 bg-[#0a0f1e]/98 px-4 py-4 space-y-1">
            {[
              { label: 'Campaigns', href: '#campaigns' },
              { label: 'About Us', href: '#about' },
              { label: 'Corporate', href: '#corporate' },
              { label: 'Contact', href: '#contact' },
            ].map(({ label, href }) => (
              <a
                key={label}
                href={href}
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm text-[#94a3b8] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                {label}
              </a>
            ))}
            <div className="pt-2 border-t border-white/5 mt-2">
              <Link
                href="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2.5 text-sm text-[#94a3b8] hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden pt-16">
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-[400px] sm:w-[700px] h-[400px] sm:h-[700px] rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #10b981 0%, #065f46 40%, transparent 70%)' }} />
          <div className="absolute -bottom-40 -left-40 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] rounded-full opacity-8"
            style={{ background: 'radial-gradient(circle, #06b6d4 0%, #0e7490 40%, transparent 70%)' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] sm:w-[900px] h-[600px] sm:h-[900px] opacity-3"
            style={{ background: 'radial-gradient(circle, #1e293b 0%, transparent 60%)' }} />
          {/* Grid lines */}
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center w-full">
          {/* Left */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-6 sm:mb-8">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              DIFC · ADGM · MAS Regulated
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] tracking-tight mb-5 sm:mb-6">
              Institutional-Grade<br />
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)' }}>
                Capital Markets
              </span><br />
              for Private Assets
            </h1>
            <p className="text-base sm:text-lg text-[#94a3b8] leading-relaxed mb-8 sm:mb-10 max-w-lg">
              CrowdVault connects accredited investors with rigorously vetted private market campaigns — infrastructure, real estate, credit, and clean energy — under full regulatory oversight.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
              <Link href="/onboarding"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl transition-all hover:shadow-xl hover:shadow-emerald-500/25 hover:-translate-y-0.5">
                Start Investing <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#campaigns"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#1e293b] hover:border-[#334155] text-[#94a3b8] hover:text-white rounded-xl transition-all">
                View Campaigns <ChevronDown className="w-4 h-4" />
              </a>
            </div>
            {/* Trust badges */}
            <div className="flex flex-wrap gap-4 sm:gap-6 mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-[#1e293b]">
              {[
                { icon: Shield, label: 'DFSA Regulated' },
                { icon: Lock, label: 'Bank-Grade Security' },
                { icon: Award, label: 'ISO 27001 Certified' },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-2 text-sm text-[#64748b]">
                  <Icon className="w-4 h-4 text-emerald-500/70" />
                  <span>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right — floating stat cards (hidden on mobile, shown on lg+) */}
          <div className="relative hidden lg:block h-[520px]">
            {/* Main card */}
            <div className="absolute top-8 right-0 w-72 glass-card rounded-2xl p-5 border border-[#1e293b]">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-[#64748b] uppercase tracking-widest">Live Portfolio</span>
                <span className="text-xs text-emerald-400 font-semibold">● Active</span>
              </div>
              <div className="text-3xl font-bold text-white mb-1">$2.4B+</div>
              <div className="text-sm text-[#64748b] mb-4">Total Capital Deployed</div>
              <div className="h-1.5 bg-[#1e293b] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full" style={{ width: '78%' }} />
              </div>
              <div className="flex justify-between text-xs text-[#64748b] mt-1.5">
                <span>78% of target</span><span>Q4 2025</span>
              </div>
            </div>
            {/* IRR card */}
            <div className="absolute top-52 left-0 w-56 glass-card rounded-2xl p-4 border border-emerald-500/20">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-[#64748b]">Avg. Net IRR</span>
              </div>
              <div className="text-2xl font-bold text-emerald-400">11.6%</div>
              <div className="text-xs text-[#64748b] mt-1">5-year portfolio average</div>
            </div>
            {/* Investors card */}
            <div className="absolute bottom-16 right-8 w-60 glass-card rounded-2xl p-4 border border-cyan-500/20">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-cyan-400" />
                <span className="text-xs text-[#64748b]">Accredited Investors</span>
              </div>
              <div className="text-2xl font-bold text-cyan-400">12,800+</div>
              <div className="text-xs text-[#64748b] mt-1">Across 34 jurisdictions</div>
            </div>
            {/* Decorative ring */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 rounded-full border border-emerald-500/10 animate-spin" style={{ animationDuration: '20s' }} />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full border border-cyan-500/10 animate-spin" style={{ animationDuration: '15s', animationDirection: 'reverse' }} />
          </div>

          {/* Mobile stat cards — shown only on small/medium screens */}
          <div className="lg:hidden grid grid-cols-2 gap-3">
            <div className="glass-card rounded-xl p-4 border border-[#1e293b]">
              <div className="text-xs text-[#64748b] mb-1">Capital Deployed</div>
              <div className="text-xl font-bold text-white">$2.4B+</div>
              <div className="text-xs text-emerald-400 mt-1">78% of target</div>
            </div>
            <div className="glass-card rounded-xl p-4 border border-emerald-500/20">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-xs text-[#64748b]">Avg. Net IRR</span>
              </div>
              <div className="text-xl font-bold text-emerald-400">11.6%</div>
            </div>
            <div className="glass-card rounded-xl p-4 border border-cyan-500/20 col-span-2">
              <div className="flex items-center gap-1.5 mb-1">
                <Users className="w-3.5 h-3.5 text-cyan-400" />
                <span className="text-xs text-[#64748b]">Accredited Investors</span>
              </div>
              <div className="text-xl font-bold text-cyan-400">12,800+ <span className="text-sm font-normal text-[#64748b]">across 34 jurisdictions</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP ─────────────────────────────────────────────────── */}
      <section className="border-y border-[#1e293b] bg-[#111827]/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-2xl sm:text-3xl font-bold text-white mb-1">{s.value}</div>
              <div className="text-xs sm:text-sm font-semibold text-[#e2e8f0] mb-0.5">{s.label}</div>
              <div className="text-xs text-[#64748b] hidden sm:block">{s.sub}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── CAMPAIGNS ───────────────────────────────────────────────────── */}
      <section id="campaigns" className="py-16 sm:py-24 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-8 sm:mb-12">
          <div>
            <div className="text-xs font-semibold text-emerald-400 uppercase tracking-widest mb-2 sm:mb-3">Featured Campaigns</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white">Active Investment<br />Opportunities</h2>
          </div>
          <Link href="/campaign-discovery"
            className="hidden md:inline-flex items-center gap-2 text-sm text-[#94a3b8] hover:text-emerald-400 transition-colors">
            View all campaigns <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Responsive campaign grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {/* Featured large card */}
          <div className="sm:col-span-2 lg:col-span-2 lg:row-span-1">
            <CampaignCard campaign={featuredCampaigns[0]} large />
          </div>
          {/* Tall right card */}
          <div className="lg:row-span-2">
            <CampaignCard campaign={featuredCampaigns[1]} tall />
          </div>
          {/* Two small cards */}
          <CampaignCard campaign={featuredCampaigns[2]} />
          <CampaignCard campaign={featuredCampaigns[3]} />
        </div>

        <div className="mt-6 sm:mt-8 text-center md:hidden">
          <Link href="/campaign-discovery"
            className="inline-flex items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors">
            View all campaigns <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* ── ABOUT US ────────────────────────────────────────────────────── */}
      <section id="about" className="py-16 sm:py-24 bg-[#111827]/40 border-y border-[#1e293b]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-16 sm:mb-20">
            <div>
              <div className="text-xs font-semibold text-cyan-400 uppercase tracking-widest mb-3">About CrowdVault™</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-5 sm:mb-6">
                Built for Institutional<br />Rigour. Designed for<br />
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #06b6d4 0%, #10b981 100%)' }}>
                  Modern Capital.
                </span>
              </h2>
              <p className="text-[#94a3b8] leading-relaxed mb-5 sm:mb-6">
                Founded in 2019 and regulated under DFSA, ADGM, and MAS frameworks, CrowdVault™ was built to solve a fundamental gap in private markets: institutional-quality deal access for accredited investors who previously lacked the network or minimum ticket size to participate.
              </p>
              <p className="text-[#94a3b8] leading-relaxed mb-6 sm:mb-8">
                Our platform combines rigorous issuer due diligence, automated KYC/AML compliance, and real-time portfolio analytics — giving investors the transparency and control they expect from public markets, applied to private assets.
              </p>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                {[
                  { label: 'Founded', value: '2019' },
                  { label: 'Headquarters', value: 'Dubai, UAE' },
                  { label: 'Regulatory Licences', value: '3 Jurisdictions' },
                  { label: 'Team Size', value: '85+ Professionals' },
                ].map(({ label, value }) => (
                  <div key={label} className="p-3 sm:p-4 rounded-xl bg-[#1a2236] border border-[#1e293b]">
                    <div className="text-xs text-[#64748b] mb-1">{label}</div>
                    <div className="font-semibold text-white text-sm sm:text-base">{value}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Values */}
            <div className="space-y-3 sm:space-y-4">
              {[
                { icon: Shield, title: 'Regulatory Compliance First', desc: 'Every campaign undergoes a 47-point compliance review before listing. Our CCO team has direct experience at DFSA and MAS.', color: 'text-emerald-400', bg: 'bg-emerald-900/20 border-emerald-800/30' },
                { icon: BarChart3, title: 'Transparent Performance Data', desc: 'Real-time IRR calculations, distribution tracking, and portfolio analytics — no black boxes, no quarterly PDFs.', color: 'text-cyan-400', bg: 'bg-cyan-900/20 border-cyan-800/30' },
                { icon: Globe, title: 'Multi-Jurisdiction Access', desc: 'Invest across UAE, Singapore, and UK-regulated instruments from a single platform with unified KYC.', color: 'text-violet-400', bg: 'bg-violet-900/20 border-violet-800/30' },
              ].map(({ icon: Icon, title, desc, color, bg }) => (
                <div key={title} className={`p-4 sm:p-5 rounded-xl border ${bg} flex gap-3 sm:gap-4`}>
                  <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${bg}`}>
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${color}`} />
                  </div>
                  <div>
                    <div className="font-semibold text-white mb-1 text-sm sm:text-base">{title}</div>
                    <div className="text-xs sm:text-sm text-[#94a3b8] leading-relaxed">{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Leadership team */}
          <div>
            <div className="text-xs font-semibold text-[#64748b] uppercase tracking-widest mb-6 sm:mb-8 text-center">Leadership Team</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {teamMembers.map((m) => (
                <div key={m.name} className="p-4 sm:p-5 rounded-2xl bg-[#111827] border border-[#1e293b] hover:border-[#334155] transition-colors">
                  <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-sm mb-3 sm:mb-4 ${m.color}`}>
                    {m.initials}
                  </div>
                  <div className="font-semibold text-white mb-0.5 text-sm sm:text-base">{m.name}</div>
                  <div className="text-xs text-emerald-400 mb-2 sm:mb-3">{m.role}</div>
                  <p className="text-xs text-[#64748b] leading-relaxed">{m.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CORPORATE DETAILS ───────────────────────────────────────────── */}
      <section id="corporate" className="py-16 sm:py-24 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-xs font-semibold text-amber-400 uppercase tracking-widest mb-3">Corporate Information</div>
        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 sm:mb-12">Regulatory &amp; Corporate<br />Structure</h2>

        <div className="grid lg:grid-cols-3 gap-5 sm:gap-6 mb-8 sm:mb-12">
          {/* Entity details */}
          <div className="lg:col-span-2 p-5 sm:p-6 rounded-2xl bg-[#111827] border border-[#1e293b]">
            <div className="flex items-center gap-3 mb-5 sm:mb-6">
              <Building2 className="w-5 h-5 text-amber-400" />
              <span className="font-semibold text-white">Legal Entities</span>
            </div>
            <div className="space-y-3 sm:space-y-4">
              {[
                { entity: 'CrowdVault Capital Ltd', reg: 'DIFC — CL2019-0847', licence: 'Category 3C — Arranging Deals in Investments', jurisdiction: 'Dubai International Financial Centre' },
                { entity: 'CrowdVault ADGM Ltd', reg: 'ADGM — FSP-2020-1123', licence: 'Financial Services Permission — Arranging & Advising', jurisdiction: 'Abu Dhabi Global Market' },
                { entity: 'CrowdVault Asia Pte Ltd', reg: 'MAS — CMS101-0234', licence: 'Capital Markets Services Licence', jurisdiction: 'Monetary Authority of Singapore' },
              ].map((e) => (
                <div key={e.entity} className="p-3 sm:p-4 rounded-xl bg-[#1a2236] border border-[#1e293b]">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4">
                    <div>
                      <div className="font-semibold text-white text-sm mb-1">{e.entity}</div>
                      <div className="text-xs text-[#64748b] mb-1">{e.jurisdiction}</div>
                      <div className="text-xs text-amber-400/80">{e.licence}</div>
                    </div>
                    <div className="sm:text-right flex-shrink-0">
                      <div className="text-xs text-[#64748b]">Reg. No.</div>
                      <div className="text-xs font-mono text-[#94a3b8]">{e.reg}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Governance */}
          <div className="space-y-4 sm:space-y-5">
            <div className="p-4 sm:p-5 rounded-2xl bg-[#111827] border border-[#1e293b]">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Award className="w-4 h-4 text-emerald-400" />
                <span className="font-semibold text-white text-sm">Certifications</span>
              </div>
              <div className="space-y-2">
                {['ISO 27001 — Information Security', 'SOC 2 Type II Certified', 'PCI-DSS Level 1 Compliant', 'GDPR & PDPA Compliant'].map((c) => (
                  <div key={c} className="flex items-center gap-2 text-xs text-[#94a3b8]">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    {c}
                  </div>
                ))}
              </div>
            </div>
            <div className="p-4 sm:p-5 rounded-2xl bg-[#111827] border border-[#1e293b]">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Landmark className="w-4 h-4 text-cyan-400" />
                <span className="font-semibold text-white text-sm">Custodian &amp; Escrow</span>
              </div>
              <div className="space-y-3 text-xs text-[#94a3b8]">
                <div><span className="text-[#64748b]">Custodian Bank</span><br /><span className="text-white">Emirates NBD — Institutional Custody</span></div>
                <div><span className="text-[#64748b]">Escrow Agent</span><br /><span className="text-white">Dentons LLP — DIFC Branch</span></div>
                <div><span className="text-[#64748b]">Auditor</span><br /><span className="text-white">Deloitte Middle East</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Investor eligibility */}
        <div className="p-5 sm:p-6 rounded-2xl bg-gradient-to-br from-emerald-900/20 to-cyan-900/10 border border-emerald-800/30">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-900/40 border border-emerald-800/40 flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-white mb-2">Investor Eligibility Requirements</div>
              <p className="text-sm text-[#94a3b8] mb-4">CrowdVault operates exclusively for accredited and professional investors as defined under applicable regulatory frameworks. Minimum eligibility criteria apply.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                {[
                  { label: 'Net Worth', value: 'USD 1M+', sub: 'Excluding primary residence' },
                  { label: 'Annual Income', value: 'USD 200K+', sub: 'Or USD 300K joint' },
                  { label: 'Min. Investment', value: 'USD 25,000', sub: 'Per campaign (varies)' },
                ].map(({ label, value, sub }) => (
                  <div key={label} className="p-3 rounded-lg bg-[#111827]/60 border border-[#1e293b]">
                    <div className="text-xs text-[#64748b] mb-1">{label}</div>
                    <div className="font-bold text-white">{value}</div>
                    <div className="text-xs text-[#64748b]">{sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/30 via-[#0a0f1e] to-cyan-900/20" />
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: 'linear-gradient(#e2e8f0 1px, transparent 1px), linear-gradient(90deg, #e2e8f0 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-xs font-semibold tracking-widest uppercase mb-6 sm:mb-8">
            <Star className="w-3 h-3" /> Accredited Investors Only
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 sm:mb-6">
            Access Private Markets<br />With Institutional Precision
          </h2>
          <p className="text-base sm:text-lg text-[#94a3b8] mb-8 sm:mb-10">
            Complete your KYC onboarding in under 15 minutes and gain access to live campaigns with target IRRs of 9–16%.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 sm:gap-4">
            <Link href="/onboarding"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl transition-all hover:shadow-2xl hover:shadow-emerald-500/30 hover:-translate-y-0.5 text-sm sm:text-base">
              Begin Onboarding <ArrowRight className="w-4 sm:w-5 h-4 sm:h-5" />
            </Link>
            <Link href="/login"
              className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 border border-[#1e293b] hover:border-[#334155] text-[#94a3b8] hover:text-white rounded-xl transition-all text-sm sm:text-base">
              Sign In to Platform
            </Link>
          </div>
        </div>
      </section>

      {/* ── CONTACT ─────────────────────────────────────────────────────── */}
      <section id="contact" className="py-12 sm:py-16 border-t border-[#1e293b] bg-[#111827]/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center">
                <Landmark className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="font-semibold text-white">CrowdVault™</span>
            </div>
            <p className="text-sm text-[#64748b] leading-relaxed mb-4">
              Institutional-grade private markets platform. Regulated. Transparent. Built for serious capital.
            </p>
            <div className="flex gap-3">
              <a href="#" className="w-8 h-8 rounded-lg bg-[#1a2236] border border-[#1e293b] flex items-center justify-center hover:border-[#334155] transition-colors">
                <svg className="w-3.5 h-3.5 text-[#64748b]" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="#" className="w-8 h-8 rounded-lg bg-[#1a2236] border border-[#1e293b] flex items-center justify-center hover:border-[#334155] transition-colors">
                <svg className="w-3.5 h-3.5 text-[#64748b]" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-[#64748b] uppercase tracking-widest mb-4">Platform</div>
            <div className="space-y-2">
              {[
                { label: 'Campaign Discovery', href: '/campaign-discovery' },
                { label: 'Investor Account', href: '/investor-account' },
                { label: 'KYC Submission', href: '/kyc-submission' },
                { label: 'Document Vault', href: '/document-vault' },
              ].map(({ label, href }) => (
                <Link key={label} href={href} className="block text-sm text-[#64748b] hover:text-[#94a3b8] transition-colors">{label}</Link>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-[#64748b] uppercase tracking-widest mb-4">Contact</div>
            <div className="space-y-3">
              {[
                { icon: MapPin, text: 'Gate District, DIFC, Dubai, UAE' },
                { icon: Mail, text: 'investors@crowdvault.io' },
                { icon: Phone, text: '+971 4 123 4567' },
              ].map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-start gap-2 text-sm text-[#64748b]">
                  <Icon className="w-4 h-4 text-emerald-500/60 flex-shrink-0 mt-0.5" />
                  {text}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 mt-10 sm:mt-12 pt-5 sm:pt-6 border-t border-[#1e293b] flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
          <p className="text-xs text-[#475569] text-center sm:text-left">© 2025 CrowdVault Capital Ltd. All rights reserved. Regulated by DFSA.</p>
          <p className="text-xs text-[#475569] text-center sm:text-right">Investment involves risk. Past performance is not indicative of future results.</p>
        </div>
      </section>
    </div>
  );
}

// ─── Campaign Card ────────────────────────────────────────────────────────────
function CampaignCard({ campaign, large, tall }: { campaign: Campaign; large?: boolean; tall?: boolean }) {
  const formatM = (n: number) => n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${(n / 1000).toFixed(0)}K`;
  const riskColors: Record<string, string> = {
    Low: 'text-emerald-400 bg-emerald-900/30 border-emerald-800/50',
    Medium: 'text-amber-400 bg-amber-900/30 border-amber-800/50',
    High: 'text-red-400 bg-red-900/30 border-red-800/50',
  };
  const catColors: Record<string, string> = {
    Infrastructure: 'text-cyan-400', 'Real Estate': 'text-amber-400',
    'Debt / Credit': 'text-violet-400', 'Clean Energy': 'text-emerald-400',
  };

  return (
    <Link href={`/campaign-detail/${campaign.id}`}
      className={`group block rounded-2xl bg-[#111827] border border-[#1e293b] hover:border-[#334155] overflow-hidden transition-all hover:shadow-xl hover:shadow-black/40 hover:-translate-y-0.5 ${tall ? 'h-full' : ''}`}>
      {/* Image area */}
      <div className={`relative overflow-hidden ${large ? 'h-40 sm:h-44' : tall ? 'h-44 sm:h-52' : 'h-32 sm:h-36'}`}>
        <Image
          src={campaign.image}
          alt={`${campaign.name} — investment campaign visual`}
          fill className="object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${campaign.tint}`} />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${riskColors[campaign.riskLevel]}`}>
            {campaign.riskLevel} Risk
          </span>
        </div>
        <div className="absolute top-3 right-3">
          <span className={`text-[10px] font-semibold ${catColors[campaign.category] || 'text-[#94a3b8]'}`}>
            {campaign.category}
          </span>
        </div>
      </div>
      {/* Content */}
      <div className="p-3 sm:p-4">
        <div className="text-xs text-[#64748b] mb-1">{campaign.issuer}</div>
        <div className="font-semibold text-white text-sm mb-2 sm:mb-3 leading-snug">{campaign.name}</div>
        {/* Progress */}
        <div className="mb-2 sm:mb-3">
          <div className="flex justify-between text-xs text-[#64748b] mb-1">
            <span>{formatM(campaign.raised)} raised</span>
            <span>{campaign.funded}%</span>
          </div>
          <div className="h-1 bg-[#1e293b] rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full transition-all"
              style={{ width: `${Math.min(campaign.funded, 100)}%` }} />
          </div>
        </div>
        {/* Metrics */}
        <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
          {[
            { label: 'Target IRR', value: campaign.irr },
            { label: 'Tenor', value: campaign.tenor },
            { label: 'Days Left', value: `${campaign.daysLeft}d` },
          ].map(({ label, value }) => (
            <div key={label} className="text-center p-1.5 sm:p-2 rounded-lg bg-[#1a2236]">
              <div className="text-xs font-semibold text-white">{value}</div>
              <div className="text-[9px] sm:text-[10px] text-[#64748b]">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </Link>
  );
}
